import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCourses } from '../contexts/CourseContext';
import { stripHtmlTags } from '../utils/textUtils';
import { getCourseStatusWithClasses } from '../utils/dateUtils';
import { supabase } from '../lib/supabase';

//카테고리 색상 매핑
const categoryColorMap: Record<string, string> = {
  group: 'bg-blue-500',
  preschool: 'bg-pink-500',
  elementary: 'bg-green-500',
  secondary: 'bg-purple-500',
  adult: 'bg-orange-500',
  special: 'bg-teal-500',
  camp: 'bg-red-500',
  international: 'bg-indigo-500'
};

// 반(class) 정보 타입
interface ProgramClassInfo {
  id: string;
  program_id: string;
  registration_start_date: string;
  registration_end_date: string;
  registration_start_time?: string;
  registration_end_time?: string;
  course_start_date: string;
  course_end_date: string;
  is_published: boolean;
}

const AllPrograms: React.FC = () => {
  const navigate = useNavigate();
  const { courses, loading } = useCourses();
  const [selectedCategory, setSelectedCategory] = useState<string>('전체');
  const [selectedStatus, setSelectedStatus] = useState<string>('전체');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [programClasses, setProgramClasses] = useState<Record<string, ProgramClassInfo[]>>({});

  const categoryOptions = ['전체', 'group', 'preschool', 'elementary', 'secondary', 'adult', 'special', 'camp', 'international'];
  const categoryNameMap: Record<string, string> = {
    '전체': '전체',
    'group': '단체체험',
    'preschool': '유치과정',
    'elementary': '초등과정',
    'secondary': '중·고등과정',
    'adult': '성인과정',
    'special': '특별과정',
    'camp': '방학캠프',
    'international': '국제교류'
  };

  const statusOptions = ['전체', '모집예정', '모집중', '모집마감', '종료'];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 모든 프로그램의 반(class) 정보 로드
  useEffect(() => {
    const loadProgramClasses = async () => {
      try {
        const { data, error } = await supabase
          .from('program_classes')
          .select('id, program_id, registration_start_date, registration_end_date, registration_start_time, registration_end_time, course_start_date, course_end_date, is_published')
          .eq('is_published', true);

        if (error) {
          console.error('Error loading program classes:', error);
          return;
        }

        // program_id별로 그룹화
        const classesMap: Record<string, ProgramClassInfo[]> = {};
        (data || []).forEach((cls: ProgramClassInfo) => {
          if (!classesMap[cls.program_id]) {
            classesMap[cls.program_id] = [];
          }
          classesMap[cls.program_id].push(cls);
        });

        setProgramClasses(classesMap);
      } catch (error) {
        console.error('Error loading program classes:', error);
      }
    };

    loadProgramClasses();
  }, []);

  // 강좌 상태 계산 (반 정보 포함)
  const getCourseStatus = (course: typeof courses[0]) => {
    const classes = programClasses[course.id] || [];
    return getCourseStatusWithClasses(course, classes);
  };

  // 필터링된 강의 목록 (수강신청용)
  const filteredCourses = courses.filter(course => {
    // 비공개 강의 제외
    if (!course.is_published) return false;

    // 수강신청 노출 설정 확인 (2025-01-27 추가)
    if (!course.show_in_registration) return false;

    const actualStatus = getCourseStatus(course);

    // 카테고리 필터
    if (selectedCategory !== '전체' && !course.category?.includes(selectedCategory as any)) {
      return false;
    }

    // 상태 필터
    if (selectedStatus !== '전체') {
      if (selectedStatus === '모집예정' && actualStatus !== 'upcoming') return false;
      if (selectedStatus === '모집중' && actualStatus !== 'recruiting') return false;
      if (selectedStatus === '모집마감' && actualStatus !== 'ongoing') return false;
      if (selectedStatus === '종료' && actualStatus !== 'completed') return false;
    }

    // 검색어 필터
    if (searchTerm && !course.courseName.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    return true;
  });

  // 수강신청 정렬: registration_order 우선, 그 다음 created_at (2025-01-27 추가)
  const sortedCourses = [...filteredCourses].sort((a, b) => {
    // registration_order가 있는 것 우선
    if (a.registration_order !== undefined && b.registration_order === undefined) return -1;
    if (a.registration_order === undefined && b.registration_order !== undefined) return 1;

    // 둘 다 registration_order가 있으면 오름차순
    if (a.registration_order !== undefined && b.registration_order !== undefined) {
      if (a.registration_order !== b.registration_order) {
        return a.registration_order - b.registration_order;
      }
    }

    // registration_order가 같거나 둘 다 없으면 created_at 내림차순 (최신순)
    return new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime();
  });

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex items-center justify-center min-h-[400px] mt-20">
          <div className="text-gray-500">로딩 중...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Page Header */}
      <div className="relative text-white py-12 sm:py-16 md:py-20 lg:py-24 mt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=1920&h=600&fit=crop&q=80"
            alt="All Programs"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-gray-200 mb-2 sm:mb-3 text-xs sm:text-sm font-medium tracking-wider">교육 프로그램</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4">수강신청</h1>
          <div className="w-16 sm:w-20 h-1 bg-white/70"></div>
        </div>
      </div>

      {/* Category Navigation Tabs */}
      <div className="bg-gray-100 border-y border-gray-200 py-3 sm:py-4 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2 sm:gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {categoryOptions.map(option => (
              <button
                key={option}
                onClick={() => setSelectedCategory(option)}
                className={`px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 md:py-2.5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  selectedCategory === option
                    ? 'bg-green-600 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-50 hover:shadow-sm'
                }`}
              >
                {categoryNameMap[option] || option}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="flex-1 bg-gray-50 px-4 sm:px-6 lg:px-8 py-8 sm:py-10 md:py-12">
        <div className="mb-8 sm:mb-10 md:mb-12 max-w-5xl mx-auto">
          <div className="bg-white rounded-xl p-5 sm:p-6 md:p-8 shadow-sm border border-gray-100">
            <div className="space-y-5 sm:space-y-6">
              {/* 상태 필터 */}
              <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-6">
                <div className="flex-shrink-0 w-full sm:w-24">
                  <label className="block text-sm font-semibold text-gray-800">상태</label>
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap gap-2">
                    {statusOptions.map(option => (
                      <button
                        key={option}
                        onClick={() => setSelectedStatus(option)}
                        className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                          selectedStatus === option
                            ? 'bg-white text-green-600 border border-green-400 shadow-[0_0_8px_rgba(34,197,94,0.15)]'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* 검색 */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                <div className="flex-shrink-0 w-full sm:w-24">
                  <label className="block text-sm font-semibold text-gray-800">검색</label>
                </div>
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="프로그램명 검색"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white transition-colors"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Programs Grid */}
      <div className="bg-gray-50 px-4 sm:px-6 lg:px-8 pb-8 sm:pb-10 md:pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 결과 카운트 */}
          <div className="mb-4 sm:mb-6">
            <p className="text-sm sm:text-base text-gray-600">
              총 <span className="font-bold text-green-600">{sortedCourses.length}</span>개의 프로그램
            </p>
          </div>

          {sortedCourses.length === 0 ? (
            <div className="text-center py-12 sm:py-16 md:py-20">
              <p className="text-gray-500 text-base sm:text-lg">서비스 준비중입니다.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
              {sortedCourses.map((course) => {
                const firstCategory = course.category && course.category.length > 0 ? course.category[0] : null;
                const categoryColor = firstCategory ? categoryColorMap[firstCategory] || 'bg-gray-500' : 'bg-gray-500';
                // 수강료 표시 (텍스트)
                const formatTuition = (tuition: string | number | null | undefined) => {
                  if (!tuition) return '무료';
                  const str = String(tuition);
                  // 숫자만 추출 (콤마, 원 제거)
                  const numStr = str.replace(/[,원]/g, '');
                  // 숫자가 아닌 경우 (예: "추후공지") 그대로 반환
                  if (isNaN(Number(numStr))) return str;
                  // 숫자인 경우 포맷팅
                  const num = Number(numStr);
                  return num === 0 ? '무료' : `${num.toLocaleString()}원`;
                };
                const tuitionDisplay = formatTuition(course.tuition);
                const actualStatus = getCourseStatus(course);

                // 상태별 뱃지 스타일
                const getStatusBadge = () => {
                  switch (actualStatus) {
                    case 'upcoming':
                      return { label: '모집예정', style: 'bg-amber-500 text-white' };
                    case 'recruiting':
                      return { label: '모집중', style: 'bg-emerald-500 text-white' };
                    case 'ongoing':
                      return { label: '모집마감', style: 'bg-gray-500 text-white' };
                    case 'completed':
                      return { label: '종료', style: 'bg-slate-500 text-white' };
                    default:
                      return { label: '모집예정', style: 'bg-amber-500 text-white' };
                  }
                };

                const statusBadge = getStatusBadge();

                return (
                  <div
                    key={course.id}
                    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                    onClick={() => navigate(`/programs/${course.id}`)}
                  >
                    {/* 이미지 */}
                    <div className="relative h-40 sm:h-44 md:h-48 bg-gray-200 overflow-hidden group">
                      {course.courseImage ? (
                        <img
                          src={course.courseImage}
                          alt={course.courseName}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-400 to-blue-500">
                          <span className="text-white text-2xl sm:text-3xl md:text-4xl font-bold opacity-50">
                            {course.courseName.charAt(0)}
                          </span>
                        </div>
                      )}
                      {/* 모집 상태 뱃지 */}
                      <div className="absolute top-3 right-3">
                        <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full shadow-md ${statusBadge.style}`}>
                          {statusBadge.label}
                        </span>
                      </div>
                    </div>

                    {/* 내용 */}
                    <div className="p-4 sm:p-5">
                      {/* 카테고리 태그 - 제목 위에 작고 subtle하게 */}
                      {firstCategory && (
                        <div className="mb-2">
                          <span className={`inline-block ${categoryColor} bg-opacity-10 ${categoryColor.replace('bg-', 'text-')} text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded border ${categoryColor.replace('bg-', 'border-')} border-opacity-30`}>
                            {categoryNameMap[firstCategory] || firstCategory}
                          </span>
                        </div>
                      )}

                      <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-2 line-clamp-2">
                        {course.courseName}
                      </h3>

                      <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 line-clamp-2">
                        {stripHtmlTags(course.courseDescription)}
                      </p>

                      <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-500">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <span className="font-medium whitespace-nowrap flex-shrink-0">신청기간:</span>
                          <span className="truncate">{course.registrationStartDate} ~ {course.registrationEndDate}</span>
                        </div>
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <span className="font-medium whitespace-nowrap flex-shrink-0">교육기간:</span>
                          <span className="truncate">{course.courseStartDate} ~ {course.courseEndDate}</span>
                        </div>
                      </div>

                      <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200 flex justify-between items-center gap-2">
                        <span className="text-base sm:text-lg font-bold text-green-600">
                          {tuitionDisplay}
                        </span>
                        <button
                          className="px-3 sm:px-4 py-1.5 sm:py-2 bg-green-600 text-white text-xs sm:text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/programs/${course.id}`);
                          }}
                        >
                          자세히 보기
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AllPrograms;
